// Mirrors @death-star/shared CouncilReviewerStatusSchema (kept inlined here
// rather than imported so the portal bundle doesn't pull in zod at runtime).
// `timeout` and `error` were added 2026-05-13; `failed` was added 2026-05-14
// (subprocess crashed before producing parseable output, distinct from a
// timeout or an API-side error). See the schema's docstring.
import { ReReviewBadge, parseVerifierResults, type VerifierResultsCounts } from './VerifierBadge.js';
type ReviewerStatus =
  | { status: 'ok' }
  | { status: 'skipped'; reason: string }
  | { status: 'timeout'; reason: string }
  | { status: 'error'; reason: string }
  | { status: 'failed'; reason: string };

interface ArtifactLike {
  ts?: unknown;
  verdict_state?: unknown;
  reviewer_status?: unknown;
  review_kind?: unknown;
  verifier_results?: unknown;
  short_circuit_reason?: unknown;
  verdict_reason?: unknown;
}

interface SlashRerunLike {
  commenter?: unknown;
  commented_at?: unknown;
}

interface ArtifactRun {
  ts: string;
  verdictState: string | null;
  reviewerStatus: Record<string, ReviewerStatus> | null;
  triggeredBy: string | null;
  reviewKind: string | null;
  verifierResults: VerifierResultsCounts | null;
  // Set when the runner short-circuited the council *before dispatching any
  // reviewer* (hard-rule path, size cap, external contributor, …). This is the
  // "intended no-op" signal — distinct from a missing/empty reviewer_status,
  // which means the breakdown was simply never recorded. See
  // diff-vader short-circuit.ts `buildShortCircuitCouncil`.
  shortCircuitReason: string | null;
  // The human-readable verdict reason that rides alongside the short-circuit
  // (e.g. names the exact hard-rule path glob). Used to surface *why*.
  verdictReason: string | null;
}

// Maps a diff-vader short-circuit reason code to a one-line explanation of why
// no reviewers ran. Keys mirror ShortCircuitReason in
// diff-vader/packages/runner/src/short-circuit.ts. Unknown codes fall back to a
// generic message so a new reason added upstream still renders sensibly.
//
// A Map (not a plain object) is deliberate: `reason` is an untrusted string
// from the artifact, so an object lookup like `LABELS[reason]` could resolve an
// inherited key (`toString`, `constructor`, ...) to a prototype function, which
// `?? fallback` wouldn't catch and React would reject as a child. `Map.get`
// only ever returns values we set, so the fallback always fires for unknowns.
const SHORT_CIRCUIT_LABELS = new Map<string, string>([
  ['hard_rules_always_human', 'a hard policy rule requires a human reviewer'],
  ['size_too_large', 'the PR exceeds the size limit and was routed straight to a human reviewer'],
  ['external_contributor', 'the author is an external contributor; policy requires a human reviewer'],
  ['predetermined_needs_human_by_size', 'the PR exceeds the auto-approve size cap, so a human review is required'],
]);

// Exported for unit tests. Not part of the component's public surface.
export function describeShortCircuit(reason: string): string {
  return SHORT_CIRCUIT_LABELS.get(reason) ?? 'the council was skipped for this run';
}

function isReviewerStatus(v: unknown): v is ReviewerStatus {
  if (!v || typeof v !== 'object') return false;
  const status = (v as { status?: unknown }).status;
  if (status === 'ok') return true;
  const reasonIsString = typeof (v as { reason?: unknown }).reason === 'string';
  if (status === 'skipped' && reasonIsString) return true;
  if (status === 'timeout' && reasonIsString) return true;
  if (status === 'error' && reasonIsString) return true;
  if (status === 'failed' && reasonIsString) return true;
  return false;
}

// Exported for unit tests. Not part of the component's public surface.
export function parseReviewerStatus(raw: unknown): Record<string, ReviewerStatus> | null {
  if (!raw || typeof raw !== 'object') return null;
  const out: Record<string, ReviewerStatus> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (isReviewerStatus(v)) out[k] = v;
  }
  return Object.keys(out).length === 0 ? null : out;
}

// Pair each artifact with the most recent slash-rerun that occurred between
// the prior artifact's timestamp (exclusive) and this artifact's timestamp
// (inclusive). The first artifact never has a trigger — it's the initial run.
//
// Pairing is best-effort. If timestamps overlap or runs are co-located (the
// rerun and the artifact land within the same second), we may attribute to
// the wrong author; we tolerate that rather than mislead with a false-precise
// claim. The PR author can always cross-reference timestamps if attribution
// matters for a specific run.
// Exported for unit tests. Not part of the component's public surface.
export function pairRunsWithTriggers(
  artifacts: ArtifactLike[],
  slashReruns: SlashRerunLike[]
): ArtifactRun[] {
  const sortedArtifacts = [...artifacts]
    .filter((a) => typeof a.ts === 'string')
    .sort((a, b) => (a.ts as string).localeCompare(b.ts as string));

  const sortedReruns = [...slashReruns]
    .filter((r) => typeof r.commented_at === 'string' && typeof r.commenter === 'string')
    .sort((a, b) => (a.commented_at as string).localeCompare(b.commented_at as string));

  const runs: ArtifactRun[] = [];
  let prevTs = '';
  for (const a of sortedArtifacts) {
    const ts = a.ts as string;
    let triggeredBy: string | null = null;
    if (runs.length > 0) {
      // Walk reruns backwards to find the most recent one in (prevTs, ts]
      for (let i = sortedReruns.length - 1; i >= 0; i--) {
        const r = sortedReruns[i]!;
        const t = r.commented_at as string;
        if (t > prevTs && t <= ts) {
          triggeredBy = r.commenter as string;
          break;
        }
      }
    }
    runs.push({
      ts,
      verdictState: typeof a.verdict_state === 'string' ? a.verdict_state : null,
      reviewerStatus: parseReviewerStatus(a.reviewer_status),
      triggeredBy,
      reviewKind: typeof a.review_kind === 'string' ? a.review_kind : null,
      verifierResults: parseVerifierResults(a.verifier_results),
      shortCircuitReason: typeof a.short_circuit_reason === 'string' ? a.short_circuit_reason : null,
      verdictReason: typeof a.verdict_reason === 'string' ? a.verdict_reason : null,
    });
    prevTs = ts;
  }
  return runs;
}

function fmtTimestamp(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
}

export function ReviewersPanel({
  artifacts,
  slashReruns,
}: {
  artifacts: ArtifactLike[];
  slashReruns: SlashRerunLike[];
}) {
  const runs = pairRunsWithTriggers(artifacts, slashReruns);

  if (runs.length === 0) {
    return <p style={{ color: 'var(--loop-neutral-500)', fontSize: '0.875rem' }}>No council runs yet.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {runs.map((run, i) => {
        const ran: string[] = [];
        const skipped: Array<{ reviewer: string; reason: string }> = [];
        const failed: Array<{ reviewer: string; status: 'timeout' | 'error' | 'failed'; reason: string }> = [];
        if (run.reviewerStatus) {
          for (const [reviewer, s] of Object.entries(run.reviewerStatus)) {
            if (s.status === 'ok') ran.push(reviewer);
            else if (s.status === 'skipped') skipped.push({ reviewer, reason: s.reason });
            else failed.push({ reviewer, status: s.status, reason: s.reason });
          }
          ran.sort((a, b) => a.localeCompare(b));
          skipped.sort((a, b) => a.reviewer.localeCompare(b.reviewer));
          failed.sort((a, b) => a.reviewer.localeCompare(b.reviewer));
        }

        const caption =
          i === 0
            ? 'initial'
            : run.triggeredBy
              ? `rerun by @${run.triggeredBy}`
              : 'rerun';

        return (
          <div key={`${run.ts}-${i}`} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--loop-neutral-200)', paddingTop: i === 0 ? 0 : 12 }}>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 528, color: 'var(--loop-neutral-900)' }}>
                Run {i + 1} <span style={{ color: 'var(--loop-neutral-600)', fontWeight: 400 }}>· {caption}</span>
                {run.reviewKind === 're-review' && <ReReviewBadge />}
              </span>
              <span style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)' }}>{fmtTimestamp(run.ts)}{run.verdictState ? ` · ${run.verdictState}` : ''}</span>
            </div>

            {!run.reviewerStatus && run.shortCircuitReason && (
              <div
                data-testid="reviewers-panel-short-circuit"
                style={{
                  fontSize: '0.8125rem',
                  color: 'var(--loop-neutral-800)',
                  borderLeft: '3px solid var(--loop-neutral-300)',
                  paddingLeft: 10,
                  margin: '4px 0',
                }}
              >
                <span style={{ fontWeight: 528, color: 'var(--loop-neutral-700)' }}>
                  Council skipped — no reviewers ran.
                </span>{' '}
                <span style={{ color: 'var(--loop-neutral-700)' }}>
                  Why: {describeShortCircuit(run.shortCircuitReason)}.
                </span>
                {run.verdictReason && (
                  <span style={{ display: 'block', marginTop: 2, color: 'var(--loop-neutral-600)' }}>
                    {run.verdictReason}
                  </span>
                )}
              </div>
            )}

            {!run.reviewerStatus && !run.shortCircuitReason && (
              <p style={{ color: 'var(--loop-neutral-500)', fontSize: '0.8125rem' }}>
                Reviewer breakdown not recorded for this run.
              </p>
            )}

            {run.reviewerStatus && ran.length > 0 && (
              <p style={{ fontSize: '0.8125rem', color: 'var(--loop-neutral-800)', margin: '4px 0' }}>
                <span style={{ color: 'var(--loop-green-700)', fontWeight: 528 }}>Ran:</span>{' '}
                <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{ran.join(' · ')}</span>
              </p>
            )}

            {run.verifierResults && (
              <p
                data-testid="reviewers-panel-verifier-summary"
                style={{ fontSize: '0.8125rem', color: 'var(--loop-neutral-800)', margin: '4px 0' }}
              >
                <span style={{ color: 'var(--loop-neutral-700)', fontWeight: 528 }}>Verifier:</span>{' '}
                <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {run.verifierResults.resolved} resolved · {run.verifierResults.unresolved} unresolved · {run.verifierResults.partial} partial · {run.verifierResults.withdrawn} withdrawn
                </span>
              </p>
            )}

            {run.reviewerStatus && skipped.length > 0 && (
              <ul style={{ marginTop: 4, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {skipped.map((s) => (
                  <li key={s.reviewer} style={{ fontSize: '0.8125rem', color: 'var(--loop-neutral-700)' }}>
                    <span style={{ color: 'var(--loop-neutral-600)', fontWeight: 528 }}>Skipped:</span>{' '}
                    <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{s.reviewer}</span>
                    <span style={{ color: 'var(--loop-neutral-600)' }}> — {s.reason}</span>
                  </li>
                ))}
              </ul>
            )}

            {run.reviewerStatus && failed.length > 0 && (
              <ul style={{ marginTop: 4, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {failed.map((s) => (
                  <li key={s.reviewer} style={{ fontSize: '0.8125rem', color: 'var(--loop-neutral-800)' }}>
                    <span style={{ color: 'var(--loop-red-700)', fontWeight: 528 }}>Failed:</span>{' '}
                    <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{s.reviewer}</span>
                    <span style={{ color: 'var(--loop-neutral-600)' }}> — {s.status}: {s.reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
